import React, { useMemo } from "react";
import { Box, Chip, Divider, Paper, Typography } from "@mui/material";
import EventIcon from "@mui/icons-material/Event";
import PeopleOutlineIcon from "@mui/icons-material/PeopleOutline";
import MeetingRoomOutlinedIcon from "@mui/icons-material/MeetingRoomOutlined";
import RestaurantOutlinedIcon from "@mui/icons-material/RestaurantOutlined";
import LocalBarOutlinedIcon from "@mui/icons-material/LocalBarOutlined";
import TvOutlinedIcon from "@mui/icons-material/TvOutlined";
import StorefrontOutlinedIcon from "@mui/icons-material/StorefrontOutlined";
import { BookingFormState, computeFinancials, fmtCAD, n, LED_PRICING } from "./bookingTypes";

const C = {
  navy: "#0d1b2a", gold: "#c9a96e", bg: "#faf8f4", white: "#fff",
  slate: "#5c6b7e", slateLt: "#9ca8b7", line: "#eeebe6",
  cream: "#f7f5f1", peach: "#ffebd3",
  greenTx: "#2d6a35", redTx: "#9b2c2c",
};

interface Props {
  form: BookingFormState;
}

/* ── Section header ── */
const SectionTitle: React.FC<{ icon: React.ReactNode; title: string }> = ({ icon, title }) => (
  <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1.5, mt: 3 }}>
    <Box sx={{ width: 28, height: 28, borderRadius: "8px", bgcolor: C.bg, display: "flex", alignItems: "center", justifyContent: "center", color: C.gold }}>{icon}</Box>
    <Typography sx={{ fontSize: 13, fontWeight: 700, color: C.navy, textTransform: "uppercase", letterSpacing: "0.06em" }}>{title}</Typography>
  </Box>
);

/* ── Line item row ── */
const LineItem: React.FC<{ label: string; detail?: string; qty?: string; amount: number; bold?: boolean }> = ({ label, detail, qty, amount, bold }) => (
  <Box sx={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", py: 0.6, px: 0.5 }}>
    <Box sx={{ flex: 1 }}>
      <Typography sx={{ fontSize: 12.5, fontWeight: bold ? 600 : 400, color: C.navy }}>{label}</Typography>
      {detail && <Typography sx={{ fontSize: 10.5, color: C.slateLt }}>{detail}</Typography>}
    </Box>
    {qty && <Typography sx={{ fontSize: 11, color: C.slateLt, minWidth: 60, textAlign: "center" }}>{qty}</Typography>}
    <Typography sx={{ fontSize: 12.5, fontWeight: bold ? 700 : 500, color: C.navy, fontVariantNumeric: "tabular-nums", minWidth: 90, textAlign: "right" }}>
      {fmtCAD(amount)}
    </Typography>
  </Box>
);

/* ── Divider line ── */
const Sep = () => <Box sx={{ height: 1, background: `linear-gradient(90deg, transparent, ${C.line}, transparent)`, my: 1 }} />;

/* ═══════════════════════════════════════════════════════════════════════════
   BOOKING REVIEW SUMMARY
   ═══════════════════════════════════════════════════════════════════════════ */
const BookingReviewSummary: React.FC<Props> = ({ form }) => {
  const fin = useMemo(() => computeFinancials(form), [form]);
  const guests = n(form.expectedGuests) || n(form.guestCount);
  const totalDeposits = form.payments.reduce((s, p) => s + n(p.amount), 0);
  const balance = fin.grandTotal - totalDeposits;

  const ledTotal = form.selectedLEDs.reduce((s, led) => s + (LED_PRICING[led] ?? 0), 0);
  const addonsTotal = form.addons.reduce((s, a) => s + a.lineTotal, 0);
  const vendorTotal = form.vendorServices.reduce((s, v) => s + v.finalPrice, 0);

  return (
    <Paper elevation={0} sx={{ borderRadius: "16px", border: `1px solid ${C.line}`, overflow: "hidden" }}>
      {/* ── Header banner ── */}
      <Box sx={{ px: 3, py: 2.5, background: `linear-gradient(135deg, ${C.navy} 0%, #1a3550 100%)` }}>
        <Typography sx={{ fontSize: 10, fontWeight: 700, color: C.gold, textTransform: "uppercase", letterSpacing: "0.1em", mb: 0.5 }}>
          Banquet Order Summary
        </Typography>
        <Typography sx={{ fontSize: 18, fontWeight: 700, color: C.white }}>
          {form.banquetName || "Untitled Event"}
        </Typography>
        {form.primaryCustomer.organization && (
          <Typography sx={{ fontSize: 12, color: "rgba(255,255,255,0.6)", mt: 0.3 }}>{form.primaryCustomer.organization}</Typography>
        )}
      </Box>

      <Box sx={{ px: 3, py: 2 }}>
        {/* ── Event Info ── */}
        <SectionTitle icon={<EventIcon sx={{ fontSize: 15 }} />} title="Event Details" />
        <Box sx={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 1, mb: 1 }}>
          {[
            { label: "Event Type", value: form.eventType || "—" },
            { label: "Event Date", value: form.eventDate || "—" },
            { label: "Time", value: form.startTime && form.endTime ? `${form.startTime} – ${form.endTime}` : "—" },
            { label: "Guests", value: `${guests}${n(form.childrenCount) > 0 ? ` + ${form.childrenCount} children` : ""}` },
            { label: "Agent", value: form.agentName || "—" },
            { label: "LCBO", value: form.lcboLicence === "yes" ? "Required" : "Not required" },
          ].map((r, i) => (
            <Box key={i} sx={{ py: 0.3 }}>
              <Typography sx={{ fontSize: 10, color: C.slateLt, fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.04em" }}>{r.label}</Typography>
              <Typography sx={{ fontSize: 12.5, color: C.navy, fontWeight: 500 }}>{r.value}</Typography>
            </Box>
          ))}
        </Box>

        {/* ── Customer Info ── */}
        <SectionTitle icon={<PeopleOutlineIcon sx={{ fontSize: 15 }} />} title="Customer Details" />
        <Box sx={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 1, mb: 1 }}>
          <Box>
            <Typography sx={{ fontSize: 10, color: C.slateLt, fontWeight: 600 }}>PRIMARY CONTACT</Typography>
            <Typography sx={{ fontSize: 12.5, color: C.navy, fontWeight: 500 }}>
              {[form.primaryCustomer.firstName, form.primaryCustomer.lastName].filter(Boolean).join(" ") || "—"}
            </Typography>
            {form.primaryCustomer.phone1 && <Typography sx={{ fontSize: 11, color: C.slateLt }}>{form.primaryCustomer.phone1}</Typography>}
            {form.primaryCustomer.email1 && <Typography sx={{ fontSize: 11, color: C.slateLt }}>{form.primaryCustomer.email1}</Typography>}
          </Box>
          {form.hasSecondaryCustomer && (
            <Box>
              <Typography sx={{ fontSize: 10, color: C.slateLt, fontWeight: 600 }}>SECONDARY CONTACT</Typography>
              <Typography sx={{ fontSize: 12.5, color: C.navy, fontWeight: 500 }}>
                {[form.secondaryCustomer.firstName, form.secondaryCustomer.lastName].filter(Boolean).join(" ") || "—"}
              </Typography>
              {form.secondaryCustomer.phone1 && <Typography sx={{ fontSize: 11, color: C.slateLt }}>{form.secondaryCustomer.phone1}</Typography>}
              {form.secondaryCustomer.email1 && <Typography sx={{ fontSize: 11, color: C.slateLt }}>{form.secondaryCustomer.email1}</Typography>}
            </Box>
          )}
        </Box>

        <Sep />

        {/* ── Description Table ── */}
        <Box sx={{ mb: 0.5 }}>
          <Box sx={{ display: "flex", justifyContent: "space-between", py: 0.5, px: 0.5, mb: 0.5 }}>
            <Typography sx={{ fontSize: 10, fontWeight: 700, color: C.slateLt, textTransform: "uppercase", flex: 1 }}>Description</Typography>
            <Typography sx={{ fontSize: 10, fontWeight: 700, color: C.slateLt, textTransform: "uppercase", minWidth: 60, textAlign: "center" }}>Qty</Typography>
            <Typography sx={{ fontSize: 10, fontWeight: 700, color: C.slateLt, textTransform: "uppercase", minWidth: 90, textAlign: "right" }}>Amount</Typography>
          </Box>

          {/* Hall */}
          {form.halls.length > 0 && (
            <>
              <SectionTitle icon={<MeetingRoomOutlinedIcon sx={{ fontSize: 14 }} />} title="Hall" />
              <LineItem label={form.halls[0].hallKey} detail={`${form.halls[0].hallCapacity} capacity`}
                qty={`${guests} guests`} amount={fin.hallSubtotal} />
              {n(form.childrenCount) > 0 && (
                <LineItem label="Children" qty={form.childrenCount} amount={0} />
              )}
            </>
          )}

          {/* Food & Menu */}
          {(n(form.foodServicePricePerPerson) > 0 || n(form.menuPricePerPerson) > 0) && (
            <>
              <SectionTitle icon={<RestaurantOutlinedIcon sx={{ fontSize: 14 }} />} title="Menu Selection" />
              {form.foodServiceOption && (
                <LineItem label={form.foodServiceOption} detail="Food service"
                  qty={`${guests} × ${fmtCAD(n(form.foodServicePricePerPerson))}`}
                  amount={n(form.foodServicePricePerPerson) * guests} />
              )}
              {form.menuOption && (
                <LineItem label={form.menuOption} detail="Menu option"
                  qty={`${guests} × ${fmtCAD(n(form.menuPricePerPerson))}`}
                  amount={n(form.menuPricePerPerson) * guests} />
              )}
            </>
          )}

          {/* Bar */}
          {(n(form.barServiceAmount) > 0 || n(form.barPackagePricePerPerson) > 0 || n(form.bartenderCount) > 0) && (
            <>
              <SectionTitle icon={<LocalBarOutlinedIcon sx={{ fontSize: 14 }} />} title="Bar Service" />
              {form.barServiceOption && (
                <LineItem label={form.barServiceOption} detail="Bar service" amount={n(form.barServiceAmount)} />
              )}
              {form.barPackageOption && (
                <LineItem label={form.barPackageOption} detail={`Bar package · ${guests} guests`}
                  qty={`${guests} × ${fmtCAD(n(form.barPackagePricePerPerson))}`}
                  amount={n(form.barPackagePricePerPerson) * guests} />
              )}
              {n(form.bartenderCount) > 0 && (
                <LineItem label={`${form.bartenderCount} Bartender${n(form.bartenderCount) > 1 ? "s" : ""}`}
                  detail={n(form.barHours) > 0 ? `${form.barHours} hours` : undefined}
                  amount={n(form.bartenderCount) * n(form.bartenderAmount)} />
              )}
            </>
          )}

          {/* LED */}
          {form.selectedLEDs.length > 0 && (
            <>
              <SectionTitle icon={<TvOutlinedIcon sx={{ fontSize: 14 }} />} title="LED & AV" />
              {form.selectedLEDs.map(led => (
                <LineItem key={led} label={led} amount={LED_PRICING[led] ?? 0} />
              ))}
            </>
          )}

          {/* Add-ons */}
          {form.addons.length > 0 && (
            <>
              <SectionTitle icon={<MeetingRoomOutlinedIcon sx={{ fontSize: 14 }} />} title="Add-ons" />
              {form.addons.map((a, i) => (
                <LineItem key={i} label={a.itemName} detail={a.category}
                  qty={`${a.quantity} × ${fmtCAD(a.unitPrice)}`} amount={a.lineTotal} />
              ))}
            </>
          )}

          {/* Vendor Services */}
          {form.vendorServices.length > 0 && (() => {
            const grouped = new Map<string, typeof form.vendorServices>();
            form.vendorServices.forEach(v => {
              const cat = v.serviceName || "Other";
              if (!grouped.has(cat)) grouped.set(cat, []);
              grouped.get(cat)!.push(v);
            });
            return (
              <>
                <SectionTitle icon={<StorefrontOutlinedIcon sx={{ fontSize: 14 }} />} title="Vendor Services" />
                {Array.from(grouped.entries()).map(([cat, items]) => (
                  <Box key={cat}>
                    <Typography sx={{ fontSize: 10, fontWeight: 600, color: C.slateLt, pl: 0.5, mt: 0.5 }}>{cat}</Typography>
                    {items.map(v => {
                      const pLabel = v.pricingType === "per_event" ? "Per Event"
                        : v.pricingType === "per_person" ? `${v.quantity} guests × ${fmtCAD(v.rate)}`
                        : v.pricingType === "per_hour" ? `${v.hours} hrs × ${fmtCAD(v.rate)}`
                        : `${v.quantity} × ${fmtCAD(v.rate)}`;
                      return (
                        <LineItem key={v.itemId} label={v.itemName} detail={`${v.vendorName} · ${pLabel}`}
                          amount={v.finalPrice} />
                      );
                    })}
                  </Box>
                ))}
              </>
            );
          })()}
        </Box>

        <Sep />

        {/* ── Financial Breakdown ── */}
        <Box sx={{ bgcolor: C.cream, borderRadius: "12px", p: 2, mt: 1 }}>
          {[
            { label: "Hall Subtotal", value: fin.hallSubtotal },
            { label: "Food & Menu", value: fin.foodSubtotal },
            { label: "Bar Service", value: fin.barSubtotal },
            ...(ledTotal > 0 ? [{ label: "LED & AV", value: ledTotal }] : []),
            ...(addonsTotal > 0 ? [{ label: "Add-ons", value: addonsTotal }] : []),
            ...(vendorTotal > 0 ? [{ label: "Vendor Services", value: vendorTotal }] : []),
          ].filter(r => r.value > 0).map((row, i) => (
            <Box key={i} sx={{ display: "flex", justifyContent: "space-between", py: "4px" }}>
              <Typography sx={{ fontSize: 12, color: C.slate }}>{row.label}</Typography>
              <Typography sx={{ fontSize: 12, fontWeight: 500, color: C.navy, fontVariantNumeric: "tabular-nums" }}>{fmtCAD(row.value)}</Typography>
            </Box>
          ))}

          {n(form.adjustment) !== 0 && (
            <Box sx={{ display: "flex", justifyContent: "space-between", py: "4px" }}>
              <Typography sx={{ fontSize: 12, color: C.slate }}>Adjustment{form.adjustmentReason ? ` (${form.adjustmentReason})` : ""}</Typography>
              <Typography sx={{ fontSize: 12, fontWeight: 500, color: n(form.adjustment) < 0 ? C.redTx : C.navy, fontVariantNumeric: "tabular-nums" }}>{fmtCAD(n(form.adjustment))}</Typography>
            </Box>
          )}
          {n(form.serviceChargePercent) > 0 && (
            <Box sx={{ display: "flex", justifyContent: "space-between", py: "4px" }}>
              <Typography sx={{ fontSize: 12, color: C.slate }}>Service Charge ({form.serviceChargePercent}%)</Typography>
              <Typography sx={{ fontSize: 12, fontWeight: 500, color: C.navy, fontVariantNumeric: "tabular-nums" }}>{fmtCAD(fin.serviceChargeAmount)}</Typography>
            </Box>
          )}
          {n(form.gratuityPercent) > 0 && (
            <Box sx={{ display: "flex", justifyContent: "space-between", py: "4px" }}>
              <Typography sx={{ fontSize: 12, color: C.slate }}>Gratuity ({form.gratuityPercent}%)</Typography>
              <Typography sx={{ fontSize: 12, fontWeight: 500, color: C.navy, fontVariantNumeric: "tabular-nums" }}>{fmtCAD(fin.gratuityAmount)}</Typography>
            </Box>
          )}

          <Box sx={{ height: 1, bgcolor: C.line, my: 1 }} />

          <Box sx={{ display: "flex", justifyContent: "space-between", py: "4px" }}>
            <Typography sx={{ fontSize: 13, fontWeight: 600, color: C.navy }}>Subtotal</Typography>
            <Typography sx={{ fontSize: 13, fontWeight: 700, color: C.navy, fontVariantNumeric: "tabular-nums" }}>{fmtCAD(fin.subtotal)}</Typography>
          </Box>
          <Box sx={{ display: "flex", justifyContent: "space-between", py: "3px" }}>
            <Typography sx={{ fontSize: 11, color: C.slateLt }}>HST (13%)</Typography>
            <Typography sx={{ fontSize: 11, color: C.slate, fontVariantNumeric: "tabular-nums" }}>{fmtCAD(fin.hstAmount)}</Typography>
          </Box>

          {/* Grand Total */}
          <Box sx={{
            display: "flex", justifyContent: "space-between", alignItems: "center",
            mt: 1.5, py: 1.5, px: 2, borderRadius: "10px",
            background: `linear-gradient(135deg, ${C.peach} 0%, #fff0d9 100%)`,
            border: `1px solid ${C.gold}25`,
          }}>
            <Typography sx={{ fontSize: 10, fontWeight: 700, color: C.gold, textTransform: "uppercase", letterSpacing: "0.08em" }}>Grand Total</Typography>
            <Typography sx={{ fontSize: 22, fontWeight: 800, color: C.navy, fontVariantNumeric: "tabular-nums" }}>{fmtCAD(fin.grandTotal)}</Typography>
          </Box>
        </Box>

        {/* ── Deposits & Balance ── */}
        {(form.payments.length > 0 || n(form.securityDepositAmount) > 0) && (
          <Box sx={{ mt: 2 }}>
            <Typography sx={{ fontSize: 11, fontWeight: 700, color: C.slateLt, textTransform: "uppercase", letterSpacing: "0.06em", mb: 1 }}>
              Deposits & Payments
            </Typography>
            {n(form.securityDepositAmount) > 0 && (
              <Box sx={{ display: "flex", justifyContent: "space-between", py: "4px" }}>
                <Typography sx={{ fontSize: 12, color: C.slate }}>Security Deposit ({form.securityDepositMethod})</Typography>
                <Typography sx={{ fontSize: 12, fontWeight: 500, color: C.navy, fontVariantNumeric: "tabular-nums" }}>{fmtCAD(n(form.securityDepositAmount))}</Typography>
              </Box>
            )}
            {form.payments.map((p, i) => (
              <Box key={i} sx={{ display: "flex", justifyContent: "space-between", py: "4px" }}>
                <Typography sx={{ fontSize: 12, color: C.slate }}>
                  Payment {i + 1} — {p.method}{p.paymentDate ? ` · ${p.paymentDate}` : ""}
                </Typography>
                <Typography sx={{ fontSize: 12, fontWeight: 500, color: C.navy, fontVariantNumeric: "tabular-nums" }}>{fmtCAD(n(p.amount))}</Typography>
              </Box>
            ))}
            {totalDeposits > 0 && (
              <>
                <Box sx={{ height: 1, bgcolor: C.line, my: 0.8 }} />
                <Box sx={{ display: "flex", justifyContent: "space-between", py: "4px" }}>
                  <Typography sx={{ fontSize: 12, fontWeight: 600, color: C.navy }}>Total Deposits</Typography>
                  <Typography sx={{ fontSize: 12, fontWeight: 700, color: C.navy, fontVariantNumeric: "tabular-nums" }}>{fmtCAD(totalDeposits)}</Typography>
                </Box>
                <Box sx={{ display: "flex", justifyContent: "space-between", py: "4px", px: 1, mt: 0.5, borderRadius: "8px", bgcolor: balance > 0 ? "#fef2f2" : balance < 0 ? "#eff6ff" : "#f0fdf4" }}>
                  <Typography sx={{ fontSize: 13, fontWeight: 700, color: balance > 0 ? C.redTx : balance < 0 ? "#1d4ed8" : C.greenTx }}>
                    {balance > 0 ? "Balance Due" : balance < 0 ? "Credit / Overpaid" : "Paid in Full"}
                  </Typography>
                  <Typography sx={{ fontSize: 14, fontWeight: 800, color: balance > 0 ? C.redTx : balance < 0 ? "#1d4ed8" : C.greenTx, fontVariantNumeric: "tabular-nums" }}>{fmtCAD(Math.abs(balance))}</Typography>
                </Box>
              </>
            )}
          </Box>
        )}

        {/* ── Notes ── */}
        {(form.publicNotes || form.privateNotes) && (
          <Box sx={{ mt: 2.5 }}>
            <Typography sx={{ fontSize: 11, fontWeight: 700, color: C.slateLt, textTransform: "uppercase", letterSpacing: "0.06em", mb: 0.5 }}>Notes</Typography>
            {form.publicNotes && <Typography sx={{ fontSize: 12, color: C.navy, mb: 0.5 }}>{form.publicNotes}</Typography>}
            {form.privateNotes && <Typography sx={{ fontSize: 11, color: C.slateLt, fontStyle: "italic" }}>Internal: {form.privateNotes}</Typography>}
          </Box>
        )}
      </Box>
    </Paper>
  );
};

export default BookingReviewSummary;
